import {
  createContact,
  deleteContact,
  getContact,
  listContacts,
  updateContact,
} from '@/services/contactService';
import { Contact, CreateContact } from '@/types/contact';
import { Query } from '@/types/query';
import { Response } from '@/types/response';
import { handleError } from '@/utils/handleError';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface ContactState {
  contacts: Contact[];
  contact: Contact | null;
  loading: boolean;
  filters: Query;
  contactsToExport: Contact[];

  fetchContacts: (userId: number, query: Query) => Promise<void>;
  createContact: (
    userId: number,
    contact: CreateContact,
    cardId?: number,
  ) => Promise<Response<Contact>>;
  showContact: (userId: number, id: number) => Promise<void>;
  updateContact: (
    userId: number,
    contactId: number,
    contact: CreateContact,
  ) => Promise<void>;
  deleteContact: (userId: number, id: number) => Promise<void>;
  bulkDeleteContacts: (userId: number, ids: number[]) => Promise<void>;
  setContactsToExport: (contacts: Contact[]) => void;
  setFilters: (filters: Partial<Query>) => void;
}

const useContactStore = create<ContactState>()(
  immer<ContactState>(set => ({
    contacts: [],
    contact: null,
    loading: false,
    filters: {
      current_page: 1,
      per_page: 10,
      sort_field: 'id',
      sort_order: 'desc',
      search: '',
      total: 0,
      last_page: 1,
    },

    contactsToExport: [],

    setFilters: (filters: Partial<Query>) => {
      set(state => {
        Object.assign(state.filters, filters);
      });
    },

    fetchContacts: async (userId: number, query: Query) => {
      try {
        set(state => {
          state.loading = true;
        });

        const response = await listContacts(userId, query);
        const { data } = response;

        if ('pagination' in data) {
          const paginatedData = data as unknown as {
            data: Contact[];
            pagination: {
              per_page: number;
              current_page: number;
              last_page: number;
              total: number;
            };
          };

          set(state => {
            state.contacts =
              query.current_page === 1
                ? paginatedData.data
                : [...state.contacts, ...paginatedData.data];

            state.filters = {
              ...state.filters,
              per_page: paginatedData.pagination.per_page,
              current_page: paginatedData.pagination.current_page,
              last_page: paginatedData.pagination.last_page,
              total: paginatedData.pagination.total,
            };
          });
        }
      } catch (error) {
        handleError(error);
        set(state => {
          state.contacts = [];
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    createContact: async (
      userId: number,
      contact: CreateContact,
      cardId?: number,
    ) => {
      try {
        set(state => {
          state.loading = true;
        });

        const response = await createContact(userId, contact, cardId);
        toast.success(response.message);

        set(state => {
          state.contact = response.data;
          state.contacts.unshift(response.data);
        });

        return response;
      } catch (error) {
        handleError(error);
        throw error;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    showContact: async (userId: number, id: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { data } = await getContact(userId, id);

        set(state => {
          state.contact = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateContact: async (
      userId: number,
      contactId: number,
      contact: CreateContact,
    ) => {
      try {
        set(state => {
          state.loading = true;
        });

        const response = await updateContact(userId, contactId, contact);
        toast.success(response.message);

        set(state => {
          state.contact = response.data;
          const index = state.contacts.findIndex(c => c.id === contactId);
          if (index !== -1) {
            Object.assign(state.contacts[index], response.data);
          }
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteContact: async (userId: number, id: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const response = await deleteContact(userId, id);
        toast.success(response.message);

        set(state => {
          state.contacts = state.contacts.filter(contact => contact.id !== id);
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    bulkDeleteContacts: async (userId: number, ids: number[]) => {
      try {
        set(state => {
          state.loading = true;
        });

        await Promise.all(ids.map(id => deleteContact(userId, id)));
        toast.success(`${ids.length} contacts supprimés avec succès`);

        set(state => {
          state.contacts = state.contacts.filter(
            contact => !ids.includes(contact.id),
          );
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    setContactsToExport: (contacts: Contact[]) => {
      set(state => {
        state.contactsToExport = contacts;
      });
    },
  })),
);

export default useContactStore;
